/**
 * Grail27 — PDP add-ons. Builds extra cart lines for the custom plate and gift wrap.
 * product-form.js calls buildCartItems() on submit of the custom buy form.
 */
if (!customElements.get('grail-product-addons')) {
  customElements.define(
    'grail-product-addons',
    class GrailProductAddons extends HTMLElement {
      connectedCallback() {
        if (this.dataset.bound === 'true') return;
        this.dataset.bound = 'true';

        this.querySelectorAll('[data-addon-toggle]').forEach((input) => {
          input.addEventListener('change', () => this.sync(input));
        });

        this.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' || event.target.tagName === 'TEXTAREA') return;
          if (!event.target.closest('[data-addon-panel]')) return;
          event.preventDefault();
          const submit = document.querySelector('#GrailProductBuyCustom [name="add"]');
          if (submit) submit.click();
        });

        this.sync();
        this.startPromo();
      }

      disconnectedCallback() {
        if (this.promoTimer) clearInterval(this.promoTimer);
      }

      startPromo() {
        const promo = this.querySelector('[data-plate-promo]');
        if (!promo) return;
        const end = Number(promo.dataset.promoEnd) * 1000;

        const tick = () => {
          if (!end || end - Date.now() <= 0) this.endPromo();
        };

        tick();
        if (this.querySelector('[data-plate-promo]')) this.promoTimer = setInterval(tick, 30000);
      }

      endPromo() {
        if (this.promoTimer) clearInterval(this.promoTimer);
        const promo = this.querySelector('[data-plate-promo]');
        if (promo) promo.remove();
        this.querySelectorAll('[data-promo-price]').forEach((el) => el.remove());
        this.querySelectorAll('[data-regular-price]').forEach((el) => (el.hidden = false));
        this.querySelectorAll('.grail-addon--promo').forEach((el) => el.classList.remove('grail-addon--promo'));
      }

      isChecked(name) {
        const input = this.querySelector(`[data-addon-toggle="${name}"]`);
        return !!(input && input.checked);
      }

      sync(changed) {
        this.querySelectorAll('[data-addon-toggle]').forEach((toggle) => {
          const panel = this.querySelector(`[data-addon-panel="${toggle.dataset.addonToggle}"]`);
          const card = toggle.closest('.grail-addon');
          if (panel) panel.hidden = !toggle.checked;
          toggle.setAttribute('aria-expanded', toggle.checked ? 'true' : 'false');
          if (card) card.classList.toggle('is-selected', toggle.checked);
        });

        const any = this.isChecked('plate') || this.isChecked('wrap');
        const note = this.querySelector('[data-addon-wallet-note]');
        if (note) note.hidden = !any;

        const wallets = document.querySelector('#GrailProductBuyCustom .grail-buy-row-bottom');
        if (wallets) wallets.classList.toggle('is-suppressed', any);

        if (!any) this.clearError();

        if (window.GrailEdd && typeof window.GrailEdd.refresh === 'function') {
          window.GrailEdd.refresh();
        }

        if (changed && changed.checked) {
          const panel = this.querySelector(`[data-addon-panel="${changed.dataset.addonToggle}"]`);
          const focusable = panel && panel.querySelector('select, input, textarea');
          if (focusable) focusable.focus();
          if (changed.dataset.addonToggle === 'plate' && focusable && focusable.tagName === 'SELECT' && typeof focusable.showPicker === 'function') {
            try {
              focusable.showPicker();
            } catch (error) {
              /* Some browsers only open a select from a direct tap. */
            }
          }
        }
      }

      modelLabel(variantId) {
        const productTitle = this.dataset.productTitle || '';
        const node = this.querySelector('[data-variant-titles]');
        if (!node) return productTitle;
        try {
          const titles = JSON.parse(node.textContent);
          const variantTitle = titles[variantId] || titles[Number(variantId)];
          if (!variantTitle || variantTitle === 'Default Title') return productTitle;
          return `${productTitle} — ${variantTitle}`;
        } catch (error) {
          return productTitle;
        }
      }

      plateToken(value) {
        return value.trim().toUpperCase().replace(/\s+/g, ' ');
      }

      buildCartItems(formData) {
        this.clearError();
        const plateOn = this.isChecked('plate');
        const wrapOn = this.isChecked('wrap');
        if (!plateOn && !wrapOn) return null;

        const variantId = formData.get('id');
        let quantity = parseInt(formData.get('quantity'), 10);
        if (!variantId || Number.isNaN(quantity) || quantity < 1) quantity = 1;

        const items = [{ id: Number(variantId), quantity }];
        const forModel = this.modelLabel(String(variantId));

        if (plateOn) {
          const plate = this.readPlate();
          if (plate.error) return plate;
          const properties = {
            'For model': forModel,
            Emirate: plate.emirate,
          };
          if (plate.code) properties['Plate code'] = plate.code;
          properties['Plate number'] = plate.number;
          properties._grail_addon = 'plate';
          if (plate.note) properties.Note = plate.note;
          items.push({
            id: Number(this.dataset.plateVariantId),
            quantity,
            properties,
          });
        }

        if (wrapOn) {
          const message = this.readMessage();
          const properties = {
            'For model': forModel,
            _grail_addon: 'giftwrap',
          };
          if (message) properties['Gift message'] = message;
          items.push({
            id: Number(this.dataset.wrapVariantId),
            quantity,
            properties,
          });
        }

        return { items };
      }

      readPlate() {
        const panel = this.querySelector('[data-addon-panel="plate"]');
        const emirateEl = panel.querySelector('[data-plate-emirate]');
        const codeEl = panel.querySelector('[data-plate-code]');
        const numberEl = panel.querySelector('[data-plate-number]');
        const noteEl = panel.querySelector('[data-plate-note]');
        const emirate = emirateEl.value.trim();
        const code = this.plateToken(codeEl.value);
        const number = this.plateToken(numberEl.value);
        let note = noteEl.value.trim();
        if (note.length > 140) note = note.slice(0, 140);

        const token = /^[A-Z0-9][A-Z0-9 -]{0,11}$/;
        const codeOk = !code || token.test(code);
        const numberOk = token.test(number);
        const valid = Boolean(emirate) && codeOk && numberOk;
        emirateEl.setAttribute('aria-invalid', emirate ? 'false' : 'true');
        codeEl.setAttribute('aria-invalid', codeOk ? 'false' : 'true');
        numberEl.setAttribute('aria-invalid', numberOk ? 'false' : 'true');

        if (!valid) {
          this.showError(this.dataset.errorPlate);
          const firstInvalid = !emirate ? emirateEl : !numberOk ? numberEl : codeEl;
          firstInvalid.focus();
          return { error: this.dataset.errorPlate };
        }

        return { emirate, code, number, note };
      }

      readMessage() {
        const field = this.querySelector('[data-gift-message]');
        if (!field) return '';
        return field.value.trim().slice(0, 200);
      }

      showError(message) {
        const error = this.querySelector('[data-addon-error]');
        if (!error) return;
        error.textContent = message || '';
        error.hidden = !message;
        if (message) this.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      clearError() {
        this.querySelectorAll('[aria-invalid="true"]').forEach((el) => el.setAttribute('aria-invalid', 'false'));
        this.showError('');
      }
    }
  );
}
